import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { useFileStore } from '@/store';
import useTranslation from '@/hooks/useTranslation';
import { isFileProcessing } from '@/lib/pollFileProcessing';

export default function FilePreparationStatus({ files: inputFiles }) {
  const files = Array.isArray(inputFiles) ? inputFiles.filter(Boolean) : [];
  const { colors } = useTheme();
  const { t } = useTranslation();
  const poll = useFileStore(state => state.actions?.pollFileProcessingStatus);
  const busy = files.some(file => file?.isUploading || isFileProcessing(file));
  const failed = files.filter(file => file?.processingStatus?.status === 'failed');
  const uncertain = failed.filter(file => file.processingStatus.statusCheckTimedOut);
  const ready = files.filter(file => !file?.isUploading && !isFileProcessing(file) && file?.processingStatus?.status !== 'failed').length;
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    setElapsed(0);
    if (!busy) return undefined;
    const start = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [busy]);
  if (!busy && !failed.length) return null;
  const elapsedLabel = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;
  return (
    <View style={{ paddingHorizontal: 10, paddingBottom: 8, gap: 4 }}>
      {busy && <Text size="s" style={{ color: colors.text_color_secondary }}>
        {t('chatInput.files.preparingProgress', { READY: ready, TOTAL: files.length, ELAPSED: elapsedLabel })}
        {' '}{t(elapsed >= 60 ? 'chatInput.files.takingLonger' : 'chatInput.files.keepTyping')}
      </Text>}
      {!!failed.length && <View accessibilityLiveRegion="polite" style={{ gap: 4 }}>
        <Text size="s" style={{ color: colors.text_color_secondary }}>
          {t(uncertain.length ? 'chatInput.files.statusUnconfirmed' : 'chatInput.files.preparationFailed')}
        </Text>
        {!!uncertain.length && poll && <Pressable accessibilityRole="button"
          accessibilityLabel={t('chatInput.files.checkStatus')}
          onPress={() => uncertain.forEach(file => { poll(file.id).catch(() => {}); })}>
          <Text size="s" style={{ color: colors.text_color_primary, textDecorationLine: 'underline' }}>
            {t('chatInput.files.checkStatus')}
          </Text>
        </Pressable>}
      </View>}
    </View>
  );
}
